import { IconColor, Nullable, OrderTableRow } from '@/Types';
import { FeedbackRatingType } from '@/Enums';
import { useMemo } from 'react';
import { dateTimeUtils, stringUtils } from '@/Utils';
import { useLocalization } from '@/Hooks';

export const useOrderStatusIcons = (purchase: Nullable<OrderTableRow>) => {
  const {
    models: { OrderStatuses },
  } = useLocalization();

  const paymentIconInfo = useMemo(() => {
    if (!purchase) {
      return { color: IconColor.Grey, tooltipText: '' };
    }

    const orderPaid = purchase.amountPaid > 0;
    const paymentRefund = purchase.refundAmount < 0;
    const paidTime = dateTimeUtils.utcStringToLocalString(purchase.paidTime);
    const refundTime = dateTimeUtils.utcStringToLocalString(purchase.refundTime);

    if (!orderPaid) {
      if (!paymentRefund) {
        return {
          color: IconColor.Grey,
          tooltipText: OrderStatuses.NotPaid,
        };
      }

      if (paymentRefund) {
        return {
          color: IconColor.Red,
          tooltipText: stringUtils.insertParamsToString(OrderStatuses.PaidRefunded, [paidTime, refundTime]),
        };
      }
    }

    if (orderPaid) {
      if (paymentRefund) {
        return {
          color: IconColor.Orange,
          tooltipText: stringUtils.insertParamsToString(OrderStatuses.PaidPartialRefunded, [paidTime, refundTime]),
        };
      }

      if (purchase.amountPaid < purchase.priceBruttoWithShipping) {
        return {
          color: IconColor.Yellow,
          tooltipText: OrderStatuses.NotPaidFull,
        };
      }

      if (purchase.amountPaid === purchase.priceBruttoWithShipping) {
        return {
          color: IconColor.Blue,
          tooltipText: stringUtils.insertParamsToString(OrderStatuses.Paid, [paidTime]),
        };
      }

      if (purchase.amountPaid > purchase.priceBruttoWithShipping) {
        return {
          color: IconColor.Lila,
          tooltipText: stringUtils.insertParamsToString(OrderStatuses.Overpaid, [paidTime]),
        };
      }
    }

    return {
      color: IconColor.Grey,
      tooltipText: OrderStatuses.NotPaid,
    };
  }, [purchase, OrderStatuses]);

  const shippingIconInfo = useMemo(() => {
    if (!purchase) {
      return {
        color: IconColor.Grey,
        tooltipText: '',
      };
    }

    const shippingTime = dateTimeUtils.utcStringToLocalString(purchase?.shippedTime);

    if (!shippingTime) {
      return {
        color: IconColor.Grey,
        tooltipText: OrderStatuses.NotShipped,
      };
    }

    return {
      color: IconColor.Blue,
      tooltipText: stringUtils.insertParamsToString(OrderStatuses.Shipped, [shippingTime]),
    };
  }, [purchase, OrderStatuses]);

  const sellerFeedbackIconInfo = useMemo(() => {
    if (!purchase) {
      return {
        color: IconColor.Grey,
        tooltipText: '',
      };
    }

    const orderItem = purchase.orderItems.find((item) => Boolean(item.sellerFeedbackTime));

    if (!orderItem || !orderItem.sellerFeedbackTime) {
      return {
        color: IconColor.Grey,
        tooltipText: OrderStatuses.FeedbackNotSubmitted,
      };
    }

    const feedbackTime = dateTimeUtils.utcStringToLocalString(orderItem.sellerFeedbackTime);

    return {
      color: IconColor.Blue,
      tooltipText: stringUtils.insertParamsToString(OrderStatuses.FeedbackSubmitted, [feedbackTime]),
    };
  }, [purchase, OrderStatuses]);

  const buyerFeedbackIconInfo = useMemo(() => {
    if (!purchase) {
      return {
        color: IconColor.Grey,
        tooltipText: '',
      };
    }

    const orderItem = purchase.orderItems.find((item) => Boolean(item.buyerFeedbackTime));

    if (!orderItem || !orderItem?.buyerFeedbackTime) {
      return {
        color: IconColor.Grey,
        tooltipText: OrderStatuses.FeedbackNotReceived,
      };
    }

    const feedbackTime = dateTimeUtils.utcStringToLocalString(orderItem.buyerFeedbackTime);
    const tooltipText = stringUtils.insertParamsToString(OrderStatuses.FeedbackReceived, [feedbackTime]);

    switch (orderItem.buyerFeedbackType) {
      case FeedbackRatingType.Positive:
        return { color: IconColor.Blue, tooltipText };
      case FeedbackRatingType.Neutral:
        return { color: IconColor.Yellow, tooltipText };
      case FeedbackRatingType.Negative:
        return { color: IconColor.Red, tooltipText };
      default:
        return { color: IconColor.Grey, tooltipText: OrderStatuses.FeedbackNotReceived };
    }
  }, [purchase, OrderStatuses]);

  return {
    paymentIconInfo,
    shippingIconInfo,
    sellerFeedbackIconInfo,
    buyerFeedbackIconInfo,
  };
};
